/**
 * @fileoverview Canonical Frontend State Shape
 * Single source of truth for application state
 */

export type ISO8601 = string;
export type Severity = "info" | "warning" | "danger" | "attention" | "neutral";

// ========== Core Metadata ==========
export interface StateMetadata {
  rev: number;                    // Monotonic revision counter for conflict resolution
  schemaVersion: string;          // Semantic version for state shape
  protoVersion: number;           // Protocol version for messages
  ts: ISO8601;                    // Last modified timestamp
}

// ========== Application State ==========
export interface AppPrefs {
  theme: "system" | "light" | "dark";
  lineWrap: boolean;
  showWhitespace: boolean;
  highContrast: boolean;
  fontSizeScale: number;          // 1.0 default, up to 2.0
  textScale: 1 | 2 | 3;           // Accessibility levels
}

export interface AppRuntime {
  lastVisitedPage?: string;       // e.g., "/pages/workspace/"
  returnTo?: string;              // Navigation context
  online: boolean;
  connectionStatus: "connected" | "connecting" | "offline" | "error";
  latencyMs?: number;
  deviceInfo: {
    width: number | null;
    height: number | null;
    touch: boolean;
    platform: string | null;
  };
}

export interface AppUIState {
  workspace: {
    drawerWidth: number;
    sidebarOpen: boolean;
  };
  code: {
    fontSize: number;
    lineWrap: boolean;
    showWhitespace: boolean;
  };
  activeModals: string[];
  toasts: Array<{
    id: string;
    type: Severity;
    message: string;
    ts: ISO8601;
  }>;
}

// ========== Projects ==========
export interface Project {
  id: string; // Unique ID (e.g., 'proj_timestamp')
  name: string; // Display name (e.g., 'My Monorepo')
  serverPath: string; // Absolute path on the server filesystem (e.g., '/Users/user/code/monorepo')
  provider?: "github" | "gitlab" | "bitbucket" | "selfhosted" | "local";
  org?: string;
  repoName?: string; // e.g., 'my-monorepo'
  branch?: string;
  proxyPort?: number; // Local proxy port if applicable
  defaultBranch?: string;
  connectionStatus: "disconnected" | "connected" | "connecting" | "error";
  fileIndexStatus: "unknown" | "indexing" | "indexed" | "error" | "stale";
  lastConnectedAt?: ISO8601;
  visibility?: "public" | "private" | "internal";
  permissions: { read: boolean; write: boolean; admin: boolean; };
  orgsAvailable?: Array<{ id: string; name: string; type: "organization" | "user"; }>;
  createdAt: ISO8601; // Timestamp when project was added
  devSessionConfig?: Record<string, any>;
  validatorConfig?: ValidatorConfig;
  updatedAt: ISO8601; // Last update timestamp
  workspaceState?: {
    selectedFiles: string[];
    instructions: string;
  includeFileTree: boolean;
  expandedFolders: string[];
    openFilePath?: string | undefined;
    openFiles: string[];
    previewFilePath: string | null;
  }
}

export interface ProjectsState {
  byId: Record<string, Project>;
  list: string[]; // Ordered array of project IDs
  activeProjectId: string | null; // ID of the currently active project
}

// ========== Indexing ==========
export interface IndexState {
  status: "idle" | "indexing" | "ready" | "error" | "stale";
  lastIndexedAt?: ISO8601;
  stats: {
    filesIndexed: number;
    symbolsExtracted: number;
    testsDiscovered: number;
    estimatedTokens: number;
  };
  fileTree: Record<string, FileMeta>;
  symbols: Record<string, SymbolInfo[]>;
  importMap: Record<string, string[]>;
  searchIndices: {
    code: any | null;
    symbols: any | null;
    tests: any | null;
  };
}

export interface FileMeta {
  path: string;
  type: "file" | "directory";
  lang?: string;
  size?: number;
  testSummary?: {
    total: number;
    passing: number;
    failing: number;
    flaky?: number;
  };
  lastModified?: ISO8601;
  lastTouchedBy?: string;
}

export interface CoverageSummary {
  lines: { total: number; covered: number; skipped: number; pct: number };
  functions: { total: number; covered: number; skipped: number; pct: number };
  branches: { total: number; covered: number; skipped: number; pct: number };
}

export interface CoverageInfo {
  path: string;
  summary: CoverageSummary;
  line_hits: Record<number, number>; // line number -> hit count
}

export type FileMetaWithCoverage = FileMeta & { coverage?: CoverageInfo };

export interface SymbolInfo {
  name: string;
  kind: "function" | "class" | "interface" | "type" | "const" | "var";
  line: number;
  signature?: string;
}

export interface FilesState {
  openFilePath?: string;
  previewFilePath: string | null;
  openFiles: string[];
  editorSelection?: {
    startLine: number;
    endLine: number;
    startCol?: number;
    endCol?: number;
    isMulti: boolean;
  };
  recentFiles: string[];
  pendingChanges: Record<string, {
    type: "added" | "modified" | "deleted";
    summary: string;
  }>;
  cachedContents: Record<string, {
    content: string;
    hash: string;
    cachedAt: ISO8601;
    coverage?: CoverageInfo;
    coverageDesynced: boolean;
  }>;
  byPath: Record<string, FileMeta>;
}

// ========== Tasks ==========
export type TaskStatus = 
  | "draft" 
  | "generating" 
  | "validating"
  | "reviewing" 
  | "validated" 
  | "needs_attention"
  | "complete" 
  | "merged"
  | "closed"
  | "error"
  | "failed";

export type TaskIntent = 
  | "Add Feature"
  | "Fix Bug"
  | "Refactor"
  | "Update Docs"
  | "Add Tests"
  | "Performance"
  | "Security";

export interface Task {
  id: string;
  title?: string;
  description?: string;
  status: TaskStatus;
  intent?: TaskIntent;
  labels: string[];
  createdAt: ISO8601;
  updatedAt: ISO8601;
  contextBundleId?: string;
  proposalIds: string[];
  currentProposalId?: string;
  timeline: Array<{
    ts: ISO8601;
    type: string;
    summary: string;
    link?: string;
  }>;
}

export interface TaskDraft {
  scope: Array<{
    type: "file" | "directory" | "symbol";
    path: string;
  }>;
  intent?: TaskIntent;
  instruction: string;
  constraints?: Record<string, any>;
  runOptions?: Record<string, any>;
  modelOverride?: {
    provider?: string;
    model?: string;
    temperature?: number;
  };
}

export interface TasksState {
  activeDraftId?: string | null;
  activeTaskId?: string;
  byId: Record<string, Task>;
  list: string[];  // Ordered task IDs
  drafts: Record<string, TaskDraft>;
  uiPrefs: {
    filters: {
      status?: TaskStatus[];
      intent?: TaskIntent[];
      labels?: string[];
      pathPrefix?: string;
      assignee?: string;
      dateRange?: { from: ISO8601; to: ISO8601 };
      mineOnly?: boolean;
    };
    sort: {
      key: "updatedAt" | "createdAt" | "status" | "title";
      direction: "asc" | "desc";
    };
    grouping: {
      byStatus?: boolean;
      density?: "comfortable" | "compact";
    };
    selection: string[];
  };
  stats: {
    total: number;
    open: number;
    inProgress: number;
    completed: number;
    archived: number;
  };
}

// ======== Mobile Sessions ==========

export type MobileSession = {
  deviceId: string;
  deviceName: string;
  pairedAt: string;
  lastUsedAt: string;
};

export type MobileSessionsState = {
  byId: Record<string, MobileSession>;
  list: string[];
};

// ========== Context Bundles ==========
export type ContextItemType = "file" | "symbol" | "summary" | "config";

export interface ContextItem {
  id: string;
  type: ContextItemType;
  ref: string;                    // path/signature/summaryId
  estimate: { tokens: number };
  sourceRank?: number;
  sourceRankOverride?: number;
  pinned?: boolean;
  excluded?: boolean;
  why?: string;
  preview?: {
    lang?: string;
    kind?: string;
    lines?: number;
  };
}

export interface ContextBundle {
  id: string;
  taskId?: string;
  createdAt: ISO8601;
  items: ContextItem[];
  budget: {
    soft: number;
    hard: number;
  };
  tokensUsed: number;
  locked: boolean;
  build?: {
    meta?: {
      ts: ISO8601;
      indexVersion?: string;
      counts?: Record<string, number>;
    };
  };
}

export interface ContextBundlesState {
  byId: Record<string, ContextBundle>;
  currentDraftId?: string;
}

// ========== Proposals ==========
export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: ISO8601;
}
  
export interface Proposal {
  id: string;
  taskId: string;
  createdAt?: ISO8601;
  updatedAt?: ISO8601;
  status: "draft" | "open" | "accepted" | "rejected" | "archived" | "closed";
  title: string;
  summary: string;
  description?: string;
  intent?: string;
  labels?: string[];
  generatingProvider?: string | null;
  chatHistory?: ChatMessage[];
  filesChanged: Array<{
    path: string;
    status: "added" | "modified" | "deleted";
    patch?: string;
    hunks: Array<{
      id: string;
      rangeBefore: { start: number; end: number };
      rangeAfter: { start: number; end: number };
      content: string;
      confidence: number;
    }>;
  }>;
  stagedFilesChanged?: any[]; // Type based on FileChanged
  parentProposalId?: string;
  userFeedback?: string;
  tokensUsed?: number;
  rawProviderResponse?: any;
}

export interface ProposalsState {
  byTaskId: Record<string, Record<string, Proposal>>;
  generating: boolean;
  generationProgress: {
    phase?: "contexting" | "generating" | "finalizing";
    percent: number;
    message?: string;
  };
}

// ========== Review & Conflicts ==========
export type ConflictResolution = "unset" | "ours" | "proposed" | "both" | "manual";
export type FileDecision = 
  | "unset"
  | "accept_ours_all"
  | "accept_proposed_all"
  | "use_both_all"
  | "clear_all";

export interface Range {
  start: number;  // 1-based line numbers
  end: number;    // inclusive
}

export interface ConflictHunk {
  id: string;
  filePath: string;
  base: Range;
  ours: Range;
  proposed: Range;
  flags: {
    trivial?: boolean;
    blocked?: boolean;
  };
  suggests?: string[];
}

export interface HunkDecision {
  hunkId: string;
  choice: ConflictResolution;
  manualText?: string;
  userId?: string;
  ts: ISO8601;
}

export interface ConflictFileSummary {
  path: string;
  lang?: string;
  counts: {
    total: number;
    resolved: number;
    unresolved: number;
  };
  lastUpdated?: ISO8601;
}

export type ConflictsStatus = "idle" | "resolving" | "ready_to_apply" | "stale";

export interface ConflictSession {
  sessionId: string;
  taskId?: string;
  base: string;      // commit SHA
  ours: string;      // commit SHA
  proposed: string;  // commit SHA
  files: Record<string, ConflictFileSummary>;
  hunks: Record<string, ConflictHunk>;
  hunkOrderByFile: Record<string, string[]>;
  decisions: Record<string, HunkDecision>;
  fileDecisions: Record<string, FileDecision>;
  status: ConflictsStatus;
  rev: number;       // conflicts-local revision
}

export type ValidationStatus = {
  status: 'pending' | 'valid' | 'invalid';
  error?: {
    line: number;
    column: number;
    message: string;
  } | null;
};

export interface ReviewState {
  currentProposalId?: string;
  conflicts?: ConflictSession;
  selections: Record<string, {
    decision: "applied" | "skipped";
    decidedAt: ISO8601;
  }>;
  filters: {
    status?: "all" | "pending" | "decided";
    file?: string;
    type?: string;
    confidence?: number;
  };
  viewMode: "unified" | "split";
  showWhitespace: boolean;
  editedContent?: Record<string, string>; // Maps hunkId to user-edited full content
  hunkValidationStatus?: Record<string, ValidationStatus>;
}

// ========== Validators ==========
export interface ValidatorConfig {
  fastIteration?: boolean;
  mergeOnSuccess?: boolean;
  lint: {
    enabled: boolean;
    ruleset: "recommended" | "strict" | "custom";
    customConfigPath?: string;
  };
  typecheck: {
    enabled: boolean;
    tsconfigPath?: string;
  };
  tests: {
    enabled: boolean;
    mode: "all" | "impacted" | "selection";
    maxConcurrency?: number;
    fastIteration?: boolean;
  };
}

export interface ValidatorProgress {
  id: string;
  taskId?: string;
  status: "idle" | "running" | "complete" | "failed";
  percent?: number;
  phase?: string;
  updatedAt: ISO8601;
}

export interface ValidatorResults {
  runId: string;
  status: string;
  lint?: ValidatorCategoryResult | null;
  typecheck?: ValidatorCategoryResult | null;
  tests?: ValidatorCategoryResult | null;
  duration?: number;
  coverage?: Record<string, CoverageInfo>;
}

export interface ValidatorsState {
  config: ValidatorConfig;
  running: {
    lint: boolean;
    typecheck: boolean;
    tests: boolean;
  };
  progress: Record<string, ValidatorProgress>;
  results: {
    byTask: Record<string, ValidatorResults>;
    latest?: ValidatorResults;
    summary: {
      lint: { passed: number; failed: number; skipped: number };
      typecheck: { passed: number; failed: number; skipped: number };
      tests: { passed: number; failed: number; skipped: number; flaky: number };
    };
  };
  lastRunAt?: ISO8601;
  logs: Array<{
    id: string;
    line: string;
    level: "debug" | "info" | "warn" | "error";
    source?: string;
    ts: ISO8601;
  }>;
}

export type FileValidationStatus = {
  path: string;
  status: "passed" | "failed" | "skipped";
  failures: any[]; // ValidatorFailure or TestFailure
}

export type ValidatorCategoryResult = {
  id: string;
  type: "lint" | "typecheck" | "tests";
  status: "passed" | "failed" | "skipped" | "error";
  passed: number;
  failed: number;
  skipped?: number;
  duration?: number;
  failures: any[]; // ValidatorFailure or TestFailure
  byFile: FileValidationStatus[];
  coverage?: Record<string, CoverageInfo>;
}

// ========== Settings ==========
export interface LLMProviderConfig {
  id: string;
  name: string;
  enabled: boolean;
  model: string;
  apiKeyEnvVar: string;
  baseUrl: string;
}

export interface LLMProfile {
  id: string;
  label: string;
  provider: string;
  model: string;
  temperature?: number;
  maxTokens?: number;
  topP?: number;
  topK?: number;
  systemPromptHash?: string;
  isDefault?: boolean;
}

export interface Settings {
  schemaVersion: string;
  profiles: {
    activeProfileId: string;
    items: Record<string, LLMProfile>;
  };
  validators: ValidatorConfig;
  indexer: {
    autoIndex: boolean;
    background: boolean;
    heuristics: {
      followImports: boolean;
      includeVendored: boolean;
      maxFileSizeKB: number;
    };
  };
  appearance: {
    theme: "system" | "light" | "dark";
    highContrast: boolean;
    fontSizeScale: number;
    lineWrap: boolean;
  };
  notifications: {
    toasts: {
      enabled: boolean;
      sound: boolean;
    };
    desktop: {
      enabled: boolean;
    };
  };
  limits: {
    tokenBudget: number;
    maxContextTokens: number;
    diffDisplayLimit: number;
    taskInstructionMax: number;
  };
    modelsRegistry: {
      providers: string[];
      lastFetchedAt?: ISO8601;
    };
    workspace: {
      interactionMode: "copy" | "gemini" | "qwen" | "codex" | "copilot" | "claude" | "llm_provider",
      llmProviderId?: string;
      yolo?: {
        enabledProviders: Record<string, boolean>;
      }
    };
    flags?: Record<string, boolean>;
    llmProviders: Record<string, LLMProviderConfig>;
  }

// ========== Notifications ==========
export type NotificationKind = 
  | "task"
  | "validator" 
  | "review"
  | "repo"
  | "index"
  | "error"
  | "info";

export interface Notification {
  id: string;
  kind: NotificationKind;
  title: string;
  body?: string;
  createdAt: ISO8601;
  readAt?: ISO8601;
  archived?: boolean;
  snoozeUntil?: ISO8601;
  action?: {
    targetPage: string;
    params?: Record<string, string>;
  };
  severity?: Severity;
}

export interface NotificationsState {
  byId: Record<string, Notification>;
  order: string[];  // newest first
  unreadCount: number;
  seenHashes: Record<string, boolean>;
  filters: {
    type?: NotificationKind;
    level?: Severity;
    unreadOnly?: boolean;
  };
}

// ========== Telemetry ==========
export interface TelemetryState {
  sessionId: string;
  events: Array<{
    name: string;
    properties?: Record<string, any>;
    ts: ISO8601;
  }>;
  counters: Record<string, number>;
}

// ========== Root State ==========
export interface RootState {
  // Metadata
  metadata: StateMetadata;
  
  // User
  user?: {
    id: string;
    name?: string;
    email?: string;
  };
  
  // Core domains
  app: {
    prefs: AppPrefs;
    runtime: AppRuntime;
    ui: AppUIState;
  };
  projects: ProjectsState;
  index: IndexState;
  files: FilesState;
  tasks: TasksState;
  contextBundles: ContextBundlesState;
  proposals: ProposalsState;
  review: ReviewState;
  validators: ValidatorsState;
  settings: Settings;
  notifications: NotificationsState;
  mobileSessions: MobileSessionsState;
  toolAvailability: Record<string, boolean>;
}

// ========== Initial State ==========
export const INITIAL_STATE: RootState = {
  // Metadata
  metadata: {
    rev: 0,
    schemaVersion: "1.0.0",
    protoVersion: 1,
    ts: new Date().toISOString(),
  },
  
  // User
  user: { id: "anonymous" },

  // Mobile Sessions
  mobileSessions: {
    byId: {},
    list: [],
  },

  
  // App
  app: {
    prefs: {
      theme: "system",
      lineWrap: true,
      showWhitespace: false,
      highContrast: false,
      fontSizeScale: 1.0,
      textScale: 1,
    },
    runtime: {
      online: true,
      connectionStatus: "connected",
      deviceInfo: {
        width: null,
        height: null,
        touch: false,
        platform: null
      }
    },
    ui: {
      workspace: {
        drawerWidth: 280,
        sidebarOpen: false
      },
      code: {
        fontSize: 14,
        lineWrap: false,
        showWhitespace: false
      },
      activeModals: [],
      toasts: []
       }
  },

  // Projects
  projects: {
    byId: {},
    list: [],
    activeProjectId: null
  },

  // Index
  index: {
    status: "idle",
    stats: {
      filesIndexed: 0,
      symbolsExtracted: 0,
      testsDiscovered: 0,
      estimatedTokens: 0
    },
    fileTree: {},
    symbols: {},
    importMap: {},
    searchIndices: {
      code: null,
      symbols: null,
      tests: null
    }
  },
  
  // Files
  files: {
    recentFiles: [],
    openFiles: [],
    previewFilePath: null,
    pendingChanges: {},
    cachedContents: {},
    byPath: {},
    editorSelection: {
      startLine: 0,
      endLine: 0,
      isMulti: false
    }
  },

  // Tasks
  tasks: {
    activeDraftId: "workspace-draft",
    byId: {},
    list: [],
    drafts: {},
    uiPrefs: {
      filters: {},
      sort: {
        key: "createdAt",
        direction: "desc"
      },
      grouping: {
        byStatus: true,
        density: "comfortable"
      },
      selection: []
    },
    stats: {
      total: 0,
      open: 0,
      inProgress: 0,
      completed: 0,
      archived: 0
    }
  },
  
  // Context Bundles
  contextBundles: {
    byId: {}
  },
  
  // Proposals
  proposals: {
    byTaskId: {},
    generating: false,
    generationProgress: {
      percent: 0
    }
  },
  
  // Review
  review: {
    selections: {},
    filters: {},
    viewMode: "unified",
    showWhitespace: false,
    hunkValidationStatus: {}
  },

  // Validators
  validators: {
    config: {
      mergeOnSuccess: false,
      lint: {
        enabled: true,
        ruleset: "recommended"
      },
      typecheck: {
        enabled: true
      },
      tests: {
        enabled: true,
        mode: "impacted",
        fastIteration: false,
      }
    },
    running: {
      lint: false,
      typecheck: false,
      tests: false
    },
    progress: {},
    results: {
      byTask: {},
      summary: {
        lint: { passed: 0, failed: 0, skipped: 0 },
        typecheck: { passed: 0, failed: 0, skipped: 0 },
        tests: { passed: 0, failed: 0, skipped: 0, flaky: 0 }
      }
    },
    logs: []
  },
  // Settings
  settings: {
    schemaVersion: "1.0.0",
    profiles: {
      activeProfileId: "default",
      items: {
        default: {
          id: "default",
          label: "Default",
          provider: "openai",
          model: "gpt-4",
          temperature: 0.7,
          maxTokens: 4096,
          isDefault: true
        }
      }
    },
    validators: {
      mergeOnSuccess: false,
      lint: {
        enabled: true,
        ruleset: "recommended"
      },
      typecheck: {
        enabled: true
      },
      tests: {
        enabled: true,
        mode: "impacted",
        fastIteration: false,
      }
    },
    indexer: {
      autoIndex: true,
      background: true,
      heuristics: {
        followImports: true,
        includeVendored: false,
        maxFileSizeKB: 500
      }
    },
    appearance: {
      theme: "system",
      highContrast: false,
      fontSizeScale: 1.0,
      lineWrap: false
    },
    notifications: {
      toasts: {
        enabled: true,
        sound: false
      },
      desktop: {
        enabled: false
      }
    },
    limits: {
      tokenBudget: 8000,
      maxContextTokens: 16000,
      diffDisplayLimit: 1000,
      taskInstructionMax: 8000
    },
    modelsRegistry: {
      providers: ["openai", "anthropic", "google"]
    },
    workspace: {
      interactionMode: "copy",
      yolo: {
        enabledProviders: {}
      }
    },
    flags: {
      isVectorDbEnabled: false,
    },
    llmProviders: {}
  },

  // Notifications
  notifications: {
    byId: {},
    order: [],
    unreadCount: 0,
    seenHashes: {},
    filters: {}
  },
  
  // Ephemeral state
  toolAvailability: {}
};